import type { ComponentPropsWithoutRef } from "react";
import {
  FormProvider,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from "react-hook-form";

export interface FormProps<
  TValues extends FieldValues,
  TContext = unknown,
  TSubmitted extends FieldValues = TValues,
> extends Omit<ComponentPropsWithoutRef<"form">, "onSubmit"> {
  /** Lo que devolvió `useForm(...)`. */
  form: UseFormReturn<TValues, TContext, TSubmitted>;
  /**
   * Qué hacer con los valores YA VALIDADOS. `Form` se encarga de
   * `handleSubmit`: la pantalla no tiene que acordarse de envolverlo, y no
   * puede olvidarse y mandar datos sin validar.
   */
  onSubmit: SubmitHandler<TSubmitted>;
}

/**
 * El formulario del sistema.
 *
 * Hace dos cosas, las dos para que los campos de dentro dejen de recibir
 * plomería por props:
 *
 *  1. Publica el formulario en contexto (`FormProvider`), que es de donde
 *     `TextField`, `EmailField`, `SubmitButton`… sacan `register`, los
 *     errores y el `isSubmitting`.
 *  2. Pinta el `<form noValidate>`. `noValidate` no es un detalle: sin él
 *     el navegador enseña SUS burbujas de validación, en su idioma y con
 *     su estética, antes de que zod llegue a opinar. La validación de este
 *     sistema es la del contrato compartido, y se ve en el campo.
 */
export function Form<
  TValues extends FieldValues,
  TContext = unknown,
  TSubmitted extends FieldValues = TValues,
>({ form, onSubmit, children, ...rest }: FormProps<TValues, TContext, TSubmitted>) {
  return (
    <FormProvider {...form}>
      <form noValidate {...rest} onSubmit={form.handleSubmit(onSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
}
